import { useNotificationContext } from 'contexts/NotificationContextProvider';
import { useCheckoutContext } from 'contexts/CheckoutContextProvider';
import useSubscribeToBundleAndProcess from 'hooks/mutations/useSubscribeToBundleAndProcess';
import { addErrorToDatadog } from 'lib/datadogError';

import { useGetBillingAddress } from './useGetBillingAddress';

export const useConfirmPurchase = () => {
  const addressDetails = useGetBillingAddress() || {};
  const { setShouldDisplay: shouldDisplayError, setCustomMessage } =
    useNotificationContext() || {};
  const {
    hasValidBillingAddress,
    hasValidUserFields,
    locationUid,
    selectedPlan = {},
    selectedTerm,
    setPaymentRequestLoading,
    stripeSetUpIntent = {},
    userDetails = {},
  } = useCheckoutContext() || {};
  const [subscribeToBundleAndProcess] = useSubscribeToBundleAndProcess();

  const getInvalidDetailsError = ({
    hasBillingAddress,
    hasContactDetails,
    hasPaymentDetails,
  } = {}) => {
    const messageStart = 'Missing valid';

    switch (true) {
      case !hasBillingAddress && !hasContactDetails && !hasPaymentDetails:
        return `${messageStart} contact, billing and payment details`;
      case !hasBillingAddress && !hasContactDetails:
        return `${messageStart} billing and contact details`;
      case !hasBillingAddress && !hasPaymentDetails:
        return `${messageStart} billing and payment details`;
      case !hasContactDetails && !hasPaymentDetails:
        return `${messageStart} contact and payment details`;
      case !hasBillingAddress:
        return `${messageStart} billing details`;
      case !hasContactDetails:
        return `${messageStart} contact details`;
      case !hasPaymentDetails:
        return `${messageStart} payment details`;
      default:
        return `${messageStart} details`;
    }
  };

  const handleErrorNotification = (message = '') => {
    setCustomMessage && setCustomMessage(message);
    shouldDisplayError && shouldDisplayError(!!message);
  };
  const resetLoading = () => setPaymentRequestLoading(false);

  const handleOnError = (
    errorMessage = 'Issue purchasing plan.',
    errorInUseConfirmPurchase = null
  ) => {
    handleErrorNotification(errorMessage);
    if (errorInUseConfirmPurchase) {
      addErrorToDatadog(
        `useConfirmPurchase error: ${errorInUseConfirmPurchase}`
      );
    }
    resetLoading();
  };

  return async () => {
    handleErrorNotification();
    setPaymentRequestLoading(true);

    const { id: stripeSetupIntentId, payment_method: stripePaymentMethodId } =
      stripeSetUpIntent || {};

    const hasBillingAddress = hasValidBillingAddress();
    const hasContactDetails = hasValidUserFields(userDetails);
    const hasPaymentDetails = !!stripeSetupIntentId && !!stripePaymentMethodId;

    const hasValidDetails =
      hasBillingAddress && hasContactDetails && hasPaymentDetails;

    if (!hasValidDetails) {
      return handleOnError(
        getInvalidDetailsError({
          hasBillingAddress,
          hasContactDetails,
          hasPaymentDetails,
        })
      );
    }

    try {
      const {
        address: street,
        city,
        country,
        state,
        zip: postalCode,
      } = addressDetails;

      const { email, firstName, lastName, organizationUid, phone } =
        userDetails;

      const input = {
        bundle: selectedPlan.plan,
        contractTerm: selectedTerm,
        billingAddress: {
          city,
          country,
          postalCode,
          street,
          state,
        },
        billingContact: {
          email,
          firstName,
          lastName,
          phone,
        },
        locationUid,
        organizationUid,
        stripePaymentMethodId,
        stripeSetupIntentId,
      };

      const response = await subscribeToBundleAndProcess({
        variables: { input },
      });

      const bundleData = response?.data?.subscribeToBundleAndProcess;

      if (bundleData) {
        resetLoading();
        return bundleData;
      } else {
        handleOnError('Issue purchasing plan.', 'no bundleData returned');
      }
    } catch (errorInUseConfirmPurchase) {
      handleOnError('Issue purchasing plan.', errorInUseConfirmPurchase);
    }
  };
};
